import { getCurrentInstance, onBeforeUnmount, onMounted } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import useModalDialog from './useModalDialog'

let saved = false

// A successful save calls this so the navigation that follows is not treated as a discard.
export function markSaved() {
  saved = true
}

// Warns before leaving a page with edits; drafts stay in memory only and are never persisted.
export default function useUnsavedChangesGuard(isDirty) {
  const { proxy } = getCurrentInstance()
  const modal = useModalDialog()
  let removeGuard = null
  const warnOnClose = event => {
    if (!isDirty()) return undefined
    event.preventDefault()
    // eslint-disable-next-line no-param-reassign
    event.returnValue = ''
    return ''
  }
  onMounted(() => {
    saved = false
    window.addEventListener('beforeunload', warnOnClose)
    removeGuard = proxy.$router.beforeEach((to, from, next) => {
      if (saved || !isDirty() || to.fullPath === from.fullPath) return next()
      return modal({
        title: i18n.t('foundation.unsavedTitle'),
        message: i18n.t('foundation.unsavedMessage'),
        okTitle: i18n.t('foundation.leave'),
        options: { cancelTitle: i18n.t('foundation.stay'), autoFocusButton: 'cancel' },
      }).then(leave => next(leave ? undefined : false))
    })
  })
  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', warnOnClose)
    if (removeGuard) removeGuard()
  })
}

// Tracks a baseline snapshot of an in-memory form and exposes the dirty check used by the guard.
export function useFormBaseline(form, settleMs = 3000) {
  let baseline = null
  let capturedAt = 0
  const capture = () => { baseline = JSON.parse(JSON.stringify(form.value)); capturedAt = Date.now() }
  const isDirty = () => baseline !== null && JSON.stringify(form.value) !== JSON.stringify(baseline)
  // Rich-text editors reformat loaded markup once; that first change is not a user edit.
  const adoptEditorNormalization = key => {
    if (baseline !== null && Date.now() - capturedAt < settleMs) baseline[key] = JSON.parse(JSON.stringify(form.value[key]))
  }
  return { capture, isDirty, adoptEditorNormalization }
}
